import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { API } from '../App';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Bell, Trophy, Flame, Dumbbell, Target, Moon, Star,
  CheckCircle, X, ChevronRight
} from 'lucide-react';
import axios from 'axios';

const getMilestoneIcon = (milestoneId) => {
  if (milestoneId?.includes('workout')) return Dumbbell;
  if (milestoneId?.includes('streak')) return Flame;
  if (milestoneId?.includes('aura')) return Star;
  if (milestoneId?.includes('recovery')) return Moon;
  if (milestoneId?.includes('rank') || milestoneId?.includes('top')) return Trophy;
  return Target;
};

const getMilestoneColor = (milestoneId) => {
  if (milestoneId?.includes('workout')) return 'text-aura-primary bg-aura-primary/20';
  if (milestoneId?.includes('streak')) return 'text-orange-400 bg-orange-500/20';
  if (milestoneId?.includes('aura')) return 'text-yellow-400 bg-yellow-500/20';
  if (milestoneId?.includes('recovery')) return 'text-purple-400 bg-purple-500/20';
  if (milestoneId?.includes('rank') || milestoneId?.includes('top')) return 'text-amber-400 bg-amber-500/20';
  return 'text-cyan-400 bg-cyan-500/20';
};

const formatTimeAgo = (dateString) => {
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const SupporterNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/supporter/notifications?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/supporter/notifications/mark-read`, 
        { mark_all: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark notifications read:', error);
    }
  };

  const markOneRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/supporter/notifications/mark-read`, 
        { notification_ids: [notificationId] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification read:', error);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notification Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-aura-text-muted" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-aura-primary text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 md:w-96 bg-aura-surface border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-400" />
              <h3 className="font-heading font-bold text-white uppercase tracking-wide text-sm">
                Milestones
              </h3>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-aura-primary hover:text-aura-primary-hover transition-colors"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-10 h-10 text-aura-text-muted mx-auto mb-3" />
                <p className="text-white font-medium">No notifications yet</p>
                <p className="text-aura-text-muted text-sm mt-1">
                  We'll notify you when your athletes hit milestones
                </p>
              </div>
            ) : (
              notifications.map((notification) => {
                const Icon = getMilestoneIcon(notification.milestone_id);
                const colorClass = getMilestoneColor(notification.milestone_id);
                
                return (
                  <div
                    key={notification.notification_id}
                    className={`p-4 border-b border-white/5 hover:bg-white/5 transition-colors ${
                      !notification.read ? 'bg-aura-primary/5' : ''
                    }`}
                    onClick={() => !notification.read && markOneRead(notification.notification_id)}
                  >
                    <div className="flex gap-3">
                      {/* Avatar/Icon */}
                      <div className={`w-10 h-10 rounded-full ${colorClass.split(' ')[1]} flex items-center justify-center flex-shrink-0`}>
                        {notification.athlete_picture ? (
                          <img 
                            src={notification.athlete_picture} 
                            alt="" 
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          <Icon className={`w-5 h-5 ${colorClass.split(' ')[0]}`} />
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-white font-medium text-sm leading-tight">
                            {notification.title}
                          </p>
                          {!notification.read && (
                            <span className="w-2 h-2 bg-aura-primary rounded-full flex-shrink-0 mt-1.5" />
                          )}
                        </div>
                        <p className="text-aura-text-muted text-sm mt-0.5">
                          {notification.message}
                        </p>
                        <p className="text-aura-text-muted/60 text-xs mt-1">
                          {formatTimeAgo(notification.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <Link
              to="/supporter/leaderboard"
              onClick={() => setIsOpen(false)}
              className="block p-3 text-center text-aura-primary hover:bg-white/5 transition-colors text-sm font-medium"
            >
              View Leaderboard
              <ChevronRight className="w-4 h-4 inline ml-1" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default SupporterNotifications;
